import React from "react";

/**
 * #618: shown when the activities load fails.
 *
 * The defect this replaces was not a missing error message — it was an empty
 * calendar grid, which a caregiver reads as "this client has no activities
 * today". That is a confident lie about a care schedule, so the copy here
 * exists to say the opposite: we do not know what is on the schedule, and you
 * should not act as though it is empty.
 *
 * Deliberately carries no status code, no exception text and no endpoint. The
 * caregiver cannot act on any of it, and the technical detail is already in
 * telemetry via the `calendar.activities.load.failed` breadcrumb.
 *
 * It also names no CAUSE. 401, 403 and 500 route here as well as a dropped
 * connection, so "this is usually a connection problem" sent the caregiver to
 * check their wifi for a server-side failure. Saying only what is true — the
 * schedule did not load, and what is on screen may be incomplete — is both
 * accurate and the part they can act on.
 */
const ScheduleLoadError = ({ error, onRetry, retryDisabled = false }) => {
  if (!error) return null;

  return (
    <div
      role="alert"
      className="schedule-load-error"
      style={{
        margin: "16px 0",
        padding: "16px 20px",
        borderRadius: "8px",
        border: "1px solid #8A6D3B",
        background: "#2B2318",
        // #484's lesson: this sits on a black surface, so the foreground has to
        // clear 4.5:1 against it rather than merely look muted.
        color: "#F5D9A0",
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>
        We couldn&apos;t load this schedule.
      </p>
      <p style={{ margin: "8px 0 0" }}>
        Please don&apos;t rely on what is shown here until it loads —
        activities may be missing from view.
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={retryDisabled}
          style={{
            marginTop: "12px",
            padding: "8px 16px",
            borderRadius: "6px",
            border: "1px solid #F5D9A0",
            background: "transparent",
            color: "#F5D9A0",
            cursor: retryDisabled ? "default" : "pointer",
            opacity: retryDisabled ? 0.6 : 1,
            fontSize: "1rem",
          }}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
};

export default ScheduleLoadError;
